import { useEffect, useState } from 'react';
import type { CourseItem } from '../shared/types';

export type EditorMode = 'PERMANENT_OVERRIDE' | 'TEMPORARY_CANCEL' | 'TEMPORARY_MODIFY' | 'TEMPORARY_ADD';

interface CourseModalProps {
  course: CourseItem | null;
  defaultDate: string;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}

const MODE_LABELS: Record<EditorMode, string> = {
  PERMANENT_OVERRIDE: '永久修改',
  TEMPORARY_CANCEL: '临时取消',
  TEMPORARY_MODIFY: '临时改期',
  TEMPORARY_ADD: '新增临时课',
};

export function CourseModal({ course, defaultDate, onClose, onSubmit }: CourseModalProps) {
  const [mode, setMode] = useState<EditorMode>('PERMANENT_OVERRIDE');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [date, setDate] = useState(defaultDate);
  const [originalUid, setOriginalUid] = useState(course?.originalUid ?? '');
  const [summary, setSummary] = useState(course?.summary ?? '');
  const [location, setLocation] = useState(course?.location ?? '');
  const [description, setDescription] = useState(course?.description ?? '');
  const [teacher, setTeacher] = useState(course?.teacher ?? '');
  const [period, setPeriod] = useState(course?.period ?? '');
  const [startTime, setStartTime] = useState(course?.startTime ?? '08:30');
  const [endTime, setEndTime] = useState(course?.endTime ?? '10:05');

  useEffect(() => {
    if (!course) return;
    setDate(course.date);
    setOriginalUid(course.originalUid);
    setSummary(course.summary);
    setLocation(course.location);
    setDescription(course.description);
    setTeacher(course.teacher);
    setPeriod(course.period);
    setStartTime(course.startTime);
    setEndTime(course.endTime);
    setMode('PERMANENT_OVERRIDE');
    setMsg('');
  }, [course]);

  const isCancelling = mode === 'TEMPORARY_CANCEL';
  const canEditTime = mode === 'TEMPORARY_MODIFY' || mode === 'TEMPORARY_ADD';

  async function submit() {
    setBusy(true);
    setMsg('');
    try {
      if (mode === 'PERMANENT_OVERRIDE') {
        await onSubmit({ mode: 'PERMANENT_OVERRIDE', originalUid, modifiedSummary: summary, modifiedLocation: location, modifiedDescription: description });
      } else if (mode === 'TEMPORARY_CANCEL') {
        await onSubmit({ mode: 'TEMPORARY', date, originalUid, action: 'CANCEL', newDetail: {} });
      } else if (mode === 'TEMPORARY_MODIFY') {
        await onSubmit({ mode: 'TEMPORARY', date, originalUid, action: 'MODIFY', newDetail: { summary, location, description, teacher, period, startTime, endTime, date, originalUid } });
      } else {
        await onSubmit({ mode: 'TEMPORARY', date, originalUid, action: 'ADD', newDetail: { summary, location, description, teacher, period, startTime, endTime, date } });
      }
      setMsg('已提交');
    } catch (err) {
      setMsg(err instanceof Error ? err.message : '提交失败');
    } finally {
      setBusy(false);
    }
  }

  if (!course) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 backdrop-blur-sm sm:items-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        {/* 头部 */}
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs text-slate-400">{course.summary} · {course.date}</p>
            <h3 className="mt-0.5 text-base font-semibold text-slate-900">调课编辑</h3>
          </div>
          <button type="button" onClick={onClose}
            className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-500 hover:bg-slate-100 transition">
            关闭
          </button>
        </div>

        {/* 模式选择 */}
        <div className="mb-4 flex gap-1.5 flex-wrap">
          {(Object.keys(MODE_LABELS) as EditorMode[]).map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={[
                'rounded-lg px-3 py-1.5 text-xs font-medium transition',
                mode === m
                  ? 'bg-blue-50 text-blue-600 border border-blue-200'
                  : 'border border-slate-200 bg-white text-slate-500 hover:text-slate-700',
              ].join(' ')}>
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="日期">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
            </Field>
            <Field label="原始 UID">
              <input value={originalUid} onChange={(e) => setOriginalUid(e.target.value)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="课程名">
              <input value={summary} onChange={(e) => setSummary(e.target.value)} disabled={isCancelling} className={inputCls} />
            </Field>
            <Field label="教师">
              <input value={teacher} onChange={(e) => setTeacher(e.target.value)} disabled={isCancelling} className={inputCls} />
            </Field>
          </div>

          <Field label="地点">
            <input value={location} onChange={(e) => setLocation(e.target.value)} disabled={isCancelling} className={inputCls} />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="开始">
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} disabled={!canEditTime} className={inputCls} />
            </Field>
            <Field label="结束">
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={!canEditTime} className={inputCls} />
            </Field>
            <Field label="节次">
              <input value={period} onChange={(e) => setPeriod(e.target.value)} disabled={isCancelling} className={inputCls} />
            </Field>
          </div>

          {!isCancelling && (
            <Field label="说明">
              <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                rows={2} className={`${inputCls} resize-none py-2`} />
            </Field>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-400">{msg || '提交后写入 D1'}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="h-9 rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-600 hover:bg-slate-100 transition">
              取消
            </button>
            <button type="button" onClick={submit} disabled={busy}
              className="h-9 rounded-xl bg-blue-500 px-4 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-50 transition">
              {busy ? '提交中…' : '确认'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-800 outline-none focus:border-blue-400 focus:bg-white disabled:opacity-40 disabled:cursor-not-allowed';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-slate-500">{label}</span>
      {children}
    </div>
  );
}
